/* eslint react-hooks/exhaustive-deps: 0 */
import React, { lazy, Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { bool, number, object, shape, string } from 'prop-types';
import classnames from 'classnames';
import { FiverrContext, I18n } from '@fiverr-private/fiverr_context';
import { DEFAULT_LOCALE } from '@fiverr-private/fiverr_context/lib/constants';
import { ErrorToast } from '@fiverr-private/orca';
import { JS_JOIN_HOOK } from '../../../utils/constants';
import { CUSTOM_ORDER_SOURCE } from '../../../utils/customOrder/constants';
import { ACTIVE } from '../../../utils/constants/userStatuses';
import { isLoggedInUser } from '../../../utils/isLoggedInUser';
import { getCustomPackageRedirectUrl } from '../../../utils/customOrder';
import { ACTIVATE_ACCOUNT_ERROR_DURATION, ACTIVATE_ACCOUNT_ERROR_TOAST_KEY } from './config';
import { buildCustomOrderProps } from './api';

import './style.scss';

const CustomOrder = lazy(() =>
    import(/* webpackChunkName: 'CustomOrder' */'@fiverr-private/custom_order/src/entries/CustomOrderEntry')
);

const getGuestAttributes = () => ({
    'data-redirect-url': getCustomPackageRedirectUrl()
});

const CustomOrderTab = (
    { responseTime, customOrder },
    { requestContext, pathfinderWrapper, logger, currencyConverter, biEvents }
) => {
    const { locale = DEFAULT_LOCALE } = useContext(FiverrContext);

    const [data, setData] = useState(null);
    const [isFetchFailed, setIsFetchFailed] = useState(false);
    const [customAttributes, setCustomAttributes] = useState({});

    const { params, current_user: currentUser } = requestContext;
    const { currentUserStatus } = customOrder;
    const biEnrichmentData = biEvents.getEnrichmentData();
    const isLoggedIn = isLoggedInUser(currentUser);
    const isGuest = !isLoggedIn;
    const allowSubmission = currentUserStatus === ACTIVE;

    useEffect(() => {
        if (!data) {
            fetchData();
        }

        if (isGuest) {
            setCustomAttributes(getGuestAttributes());
        }
    }, [data]);

    const fetchData = useCallback(() => {
        const { slug: gigSlug, username: sellerUsername } = params;

        buildCustomOrderProps({ sellerUsername, gigSlug, pathfinderWrapper, logger })
            .then(setData)
            .catch(() => setIsFetchFailed(true));
    }, [requestContext]);

    const onClick = () => {
        if (allowSubmission || isGuest) {return;}

        ErrorToast.raise({
            message: <I18n k={ACTIVATE_ACCOUNT_ERROR_TOAST_KEY} params={{ locale }}/>,
            duration: ACTIVATE_ACCOUNT_ERROR_DURATION
        });
    };

    const retryFetchData = () => {
        setIsFetchFailed(false);
        fetchData();
    };

    const currency = currencyConverter.getCurrency();
    const classNames = classnames('custom-order-tab', {
        'is-loading': !data && !isFetchFailed,
        [JS_JOIN_HOOK]: isGuest
    });

    return (
        <div className={classNames} {...customAttributes} onClick={onClick}>
            {data &&
            <Suspense fallback={<div/>}>
                <CustomOrder {...data}
                    currency={currency}
                    responseTime={responseTime}
                    ctaText={<I18n k={'gig_page_perseus.custom_order.send'}/>}
                    isLoggedIn={isLoggedIn}
                    allowSubmission={allowSubmission}
                    source={CUSTOM_ORDER_SOURCE}
                    biEnrichmentData={biEnrichmentData}
                    isFourthPackageTest/>
            </Suspense>
            }
            {isFetchFailed &&
            <div className="submit-retry">
                <I18n k={'gig_page_perseus.custom_order.something_wrong'}/>
                <button onClick={retryFetchData}>
                    <I18n k={'gig_page_perseus.custom_order.try_again'}/>
                </button>
            </div>
            }
        </div>
    );
};

CustomOrderTab.propTypes = {
    responseTime: number,
    customOrder: shape({
        currentUserStatus: string,
        allowCustomOrders: bool
    })
};

CustomOrderTab.contextTypes = {
    requestContext: object,
    currencyConverter: object,
    pathfinderWrapper: object,
    logger: object,
    biEvents: object
};

export default CustomOrderTab;
